import { graphql } from '../../gql';
import { useApiMutation, useApiQuery } from '../../query/hooks';
import { FC, useEffect } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { z } from 'zod';
import { Button, DialogActions, DialogContent, DialogTitle } from '@mui/material';
import { Stack } from '@mui/material';
import { ControlledFormField } from '../../form/ControlledFormField';
import { JourneySelect } from '../../journey/JourneySelect';
import { PhaseSelect } from '../../journey/PhaseSelect';
import { ActivitySelect } from '../../journey/ActivitySelect';
import { Divider } from '@mui/material';
import { FormErrors } from '../../form/FormErrors';
import { useTypedForm } from '../../../../shared/fe/hooks/useForm';
import { useModalContext } from '../../modal/ModalContext';
import { mutationResponseHandler } from '../../utils/mutationResponseHandler';
import { useSnackbar } from '../../store/snackbar';

const getActivityQuery = graphql(`
	query createMetricStepLocation_getActivity($id: UUID!) {
		getActivity(by: { id: $id }) {
			id
			touchpoint {
				phase {
					id
					journey {
						id
					}
				}
			}
		}
	}
`);

const assignMetricMutation = graphql(`
	mutation assignMetricModal_updateMetric($id: UUID!, $data: ContentMetricUpdateInput!) {
		updateContentMetric(by: { id: $id }, data: $data) {
			ok
			errorMessage
		}
	}
`);

const createFormSchema = () =>
	z.object({
		journeyId: z.string(),
		phaseId: z.string(),
		activityId: z.string(),
	});

type FormValues = z.infer<ReturnType<typeof createFormSchema>>;

const metricAssignmentModalSchema = z.object({
	contentMetricId: z.string(),
	activityId: z.string().optional(),
});

const MetricAssignmentModal: FC<z.infer<typeof metricAssignmentModalSchema>> = ({ contentMetricId, activityId }) => {
	const { closeModal } = useModalContext();
	const intl = useIntl();
	const { openSnackbar } = useSnackbar();

	const form = useTypedForm({
		schema: createFormSchema(),
	});

	const { data, isLoading } = useApiQuery(getActivityQuery, {
		enabled: activityId != null,
		variables: {
			id: activityId ?? '',
		},
	});

	const { mutateAsync: updateContentMetricAsync } = useApiMutation(assignMetricMutation, {
		onSuccess: (result) => {
			mutationResponseHandler({
				result: result.updateContentMetric.ok,
				toastMessages: {
					success: intl.formatMessage({ id: 'form.assign-metric.success' }),
					error: intl.formatMessage({ id: 'form.assign-metric.error' }),
				},
				callback: closeModal,
				openSnackbar,
			});
		},
	});

	const handleSubmit = (values: FormValues) => {
		updateContentMetricAsync({
			id: contentMetricId,
			data: {
				activities: [
					{
						connect: {
							id: values.activityId,
						},
					},
					{
						update: {
							by: {
								id: values.activityId,
							},
							data: {
								touchpoint: {
									update: {
										phase: {
											update: {
												contentMetrics: [
													{
														connect: {
															id: contentMetricId,
														},
													},
												],
											},
										},
									},
								},
							},
						},
					},
				],
			},
		});
	};

	useEffect(() => {
		const journeyId = data?.getActivity?.touchpoint?.phase?.journey?.id;
		const phaseId = data?.getActivity?.touchpoint?.phase?.id;
		const activityId = data?.getActivity?.id;

		if (journeyId != null && phaseId != null && activityId != null) {
			form.reset({
				journeyId,
				phaseId,
				activityId,
			});
		}
	}, [form, data]);

	const values = form.watch();

	return (
		<>
			<DialogTitle>
				<FormattedMessage id={'form.metric-assignment.title'} />
			</DialogTitle>
			<Divider />
			<form noValidate onSubmit={form.handleSubmit(handleSubmit)}>
				<DialogContent>
					<Stack spacing={3}>
						<ControlledFormField
							form={form}
							name={'journeyId'}
							label={intl.formatMessage({ id: 'form.assign-metric.label.journey' })}
							description={intl.formatMessage({ id: 'form.assign-metric.label.journey.description' })}
							render={(props) => <JourneySelect loading={isLoading} {...props} />}
						/>

						<ControlledFormField
							form={form}
							name={'phaseId'}
							label={intl.formatMessage({ id: 'form.assign-metric.label.phase' })}
							render={(props) => <PhaseSelect loading={isLoading} journeyId={values.journeyId} {...props} />}
						/>

						<ControlledFormField
							form={form}
							name={'activityId'}
							label={intl.formatMessage({ id: 'form.assign-metric.label.activity' })}
							render={(props) => <ActivitySelect loading={isLoading} phaseId={values.phaseId} {...props} />}
						/>

						<FormErrors form={form} />
					</Stack>
				</DialogContent>
				<DialogActions sx={{ p: 2.5 }}>
					<Stack direction={'row'} spacing={2} alignItems={'center'} width={'100%'} justifyContent={'end'}>
						<Button color={'secondary'} onClick={closeModal}>
							<FormattedMessage id={'common.cancel'} />
						</Button>
						<Button type={'submit'} variant={'contained'} disabled={isLoading}>
							<FormattedMessage id={'common.save'} />
						</Button>
					</Stack>
				</DialogActions>
			</form>
		</>
	);
};

export { metricAssignmentModalSchema, MetricAssignmentModal };
